/**
 * Numbering MODE settings only (manual vs auto counters) — a system setting, not a
 * per-matter numbers-entry action.
 *
 * Client number, matter number, and matter name entry moved to /numbering
 * (pages/IntakeNumbering.jsx) and is now exclusively an Intake action: the
 * `setMatterNumbers`/`assignMatterNumbers` callables reject any caller who is not on
 * the Intake roster, including admin/superadmin (see functions/index.js's
 * requireIntake). This page keeps only the manual/auto mode toggle, which remains an
 * admin setting.
 *
 * The firm assigns numbers by hand today, so `manual` is the default: the workflow still
 * runs, and Intake types the numbers in once Accounting has them. `auto` uses
 * transaction-safe Firestore counters. A future Aderant integration slots in as a third
 * mode — see the TODO in functions/src/numbering/numberingService.js.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CallStatus, useCallable } from '../../lib/useCallable';
import { useDoc } from '../../lib/useFirestore';
import { preserveGeneralSettings } from '../../lib/generalSettings';

const field = 'w-full rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm';

export default function AdminNumbering() {
  const { data: stored, loading } = useDoc('settings/numbering');
  const { data: general, loading: loadingGeneral } = useDoc('settings/general');
  const { run, busy, error, saved } = useCallable();
  const distributionSettings = useCallable();
  const [settings, setSettings] = useState(null);
  const [distributionText, setDistributionText] = useState(null);

  useEffect(() => {
    if (stored && !settings) setSettings(structuredClone(stored));
  }, [stored, settings]);
  useEffect(() => {
    if (!loadingGeneral && distributionText === null) {
      setDistributionText((general?.finalDistributionEmails || []).join('\n'));
    }
  }, [general, loadingGeneral, distributionText]);

  if (loading || !settings) return <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>;

  const patch = (key, value) => setSettings({ ...settings, [key]: value });
  const save = () => run('adminUpdateSettings', { docId: 'numbering', value: settings });
  const saveDistribution = () => {
    // Comma- or newline-separated, blanks skipped, only kept if it looks like an email.
    const emails = distributionText
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter((s) => s && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s));
    return distributionSettings.run('adminUpdateSettings', {
      docId: 'general',
      value: preserveGeneralSettings(general, { finalDistributionEmails: emails }),
    });
  };

  return (
    <div className="space-y-4">
      <section className="rounded-lg bg-white dark:bg-slate-900 p-4 shadow">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Numbering mode</h2>
          <CallStatus busy={busy} error={error} saved={saved} />
        </div>

        <div className="mt-3 space-y-2">
          {[
            ['manual', 'Manual — an administrator types the numbers in after approval.'],
            ['auto', 'Automatic — the system allocates the next number from a counter.'],
          ].map(([value, label]) => (
            <label key={value} className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                className="mt-0.5"
                name="mode"
                checked={settings.mode === value}
                onChange={() => patch('mode', value)}
              />
              {label}
            </label>
          ))}
          <p className="text-xs text-slate-500 dark:text-slate-400">
            A third mode that pulls numbers from Aderant is planned; the numbering service
            is already written against a pluggable interface so it can be added without
            touching the approval engine.
          </p>
        </div>

        {settings.mode === 'auto' && (
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            {[
              ['clientPrefix', 'Client number prefix'],
              ['clientPadding', 'Client digits'],
              ['clientCounterStart', 'Client counter starts at'],
              ['matterPrefix', 'Matter number prefix'],
              ['matterPadding', 'Matter digits'],
              ['matterCounterStart', 'Matter counter starts at'],
            ].map(([key, label]) => (
              <label key={key} className="text-xs text-slate-600 dark:text-slate-300">
                {label}
                <input
                  className={`${field} mt-1`}
                  type={key.includes('Prefix') ? 'text' : 'number'}
                  value={settings[key] ?? ''}
                  onChange={(e) =>
                    patch(key, key.includes('Prefix') ? e.target.value : Number(e.target.value))
                  }
                />
              </label>
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="mt-4 rounded bg-firm-700 dark:bg-firm-500 px-4 py-2 text-sm font-medium text-white hover:bg-firm-900 dark:hover:bg-firm-400 disabled:opacity-50"
        >
          Save numbering settings
        </button>
      </section>

      <section className="rounded-lg bg-white dark:bg-slate-900 p-4 shadow">
        <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Entering numbers</h2>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
          Client/matter numbers and the matter name are now entered by Intake at{' '}
          <Link to="/numbering" className="text-firm-700 dark:text-firm-100 hover:underline">
            /numbering
          </Link>
          . This is enforced server-side — only the Intake role may set these fields,
          including for admins and superadmins.
        </p>
      </section>

      <section className="rounded-lg bg-white dark:bg-slate-900 p-4 shadow">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Final numbered package</h2>
          <CallStatus {...distributionSettings} />
        </div>
        <label className="mt-1 block text-xs text-slate-500 dark:text-slate-400" htmlFor="final-distribution-emails">
          Send the final numbered package to (comma-separated emails — billing, records,
          walls/IT, or anyone else who needs the complete file once numbers are assigned)
        </label>
        <textarea
          id="final-distribution-emails"
          className={`${field} mt-2`}
          rows={3}
          placeholder="e.g. billing@aalrr.com, records@aalrr.com, walls-it@aalrr.com"
          value={distributionText ?? ''}
          onChange={(e) => setDistributionText(e.target.value)}
        />
        <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
          Sent once, right after a matter's client/matter numbers are assigned (entered by
          Intake at /numbering, or allocated automatically depending on the mode above),
          with a fresh copy of the full-record PDF reflecting the final numbers. Leave
          blank to send nothing. This is separate from the submitter's own
          "fully approved" email (Admin → Workflow → Matter-completion email).
        </p>
        <button
          type="button"
          onClick={saveDistribution}
          disabled={distributionSettings.busy || distributionText === null}
          className="mt-3 rounded border border-slate-300 dark:border-slate-600 px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50"
        >
          Save final-package setting
        </button>
      </section>
    </div>
  );
}
